import { Route } from "@/routes/dashboard/settings/alert-management/alerts/$alertId/route.tsx";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TabItem } from "@/components/custom/tabs/nav-tabs.tsx";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/client";
import { usePermissions } from "@/hooks/use-permissions";

// The tabs switch the detail panel beside the alerts list, so they keep the list's search params: clearing
// them drops its `position`, and the list then re-selects the open alert by navigating to the sub-tab it
// last matched -- bouncing a click on Overview straight back to Escalation.
export const useAlertTabs = (): TabItem[] => {
  const { t } = useTranslation();
  const { alertId } = Route.useRouteContext();
  const { permissions } = usePermissions();
  const { data } = useQuery(
    client.alerts.getAlertDetails.asQueryOptions({ id: alertId }),
  );

  const tabs: TabItem[] = [
    {
      title: t("overview"),
      path: "overview",
      requiredPermissions: ["alerts.view"],
      search: true,
    },
    // Only alert types with an escalated version can have an escalation. The tab strip renders every tab it is
    // given, so a role without the (Edgify-only) permission must not be given this one: its route would
    // only bounce back to Overview.
    ...(data?.escalatedSpecId && permissions?.alerts?.escalation?.view
      ? [
          {
            title: t("escalation"),
            path: "escalation",
            requiredPermissions: ["alerts.escalation.view"],
            search: true,
          },
        ]
      : []),
  ];
  return tabs;
};

export const AlertTabs = () => {
  const tabs = useAlertTabs();
  const { alertId } = Route.useRouteContext();
  const matchRoute = useMatchRoute();
  const selected = tabs.find((tab) =>
    matchRoute({ from: Route.fullPath, to: tab.path, fuzzy: true }),
  )?.path;

  return (
    <Tabs value={selected}>
      <TabsList>
        {tabs.map((tab) => (
          <Link
            key={tab.path}
            from={Route.fullPath}
            to={tab.path}
            params={{ alertId: `${alertId}` }}
            search={tab.search}
          >
            <TabsTrigger value={tab.path ?? ""}>{tab.title}</TabsTrigger>
          </Link>
        ))}
      </TabsList>
    </Tabs>
  );
};
